import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Lock, Eye, EyeSlash, CheckCircle, EnvelopeSimple, FloppyDisk } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [show, setShow] = useState({ current: false, new: false, confirm: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [mailForm, setMailForm] = useState({
    enabled: false,
    smtp_host: "smtpout.secureserver.net",
    smtp_port: 465,
    smtp_security: "ssl",
    smtp_username: "",
    smtp_password: "",
    smtp_from: "",
    mail_to: "",
    notify_leads: true,
    notify_petitions: true,
  });
  const [hasMailPassword, setHasMailPassword] = useState(false);
  const [mailSubmitting, setMailSubmitting] = useState(false);
  const [mailError, setMailError] = useState("");
  const [mailSuccess, setMailSuccess] = useState("");

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/mail-settings`)
      .then((r) => r.json())
      .then((data) => {
        setMailForm((current) => ({
          ...current,
          enabled: !!data.enabled,
          smtp_host: data.smtp_host || "smtpout.secureserver.net",
          smtp_port: data.smtp_port || 465,
          smtp_security: data.smtp_security || "ssl",
          smtp_username: data.smtp_username || "",
          smtp_password: "",
          smtp_from: data.smtp_from || "",
          mail_to: data.mail_to || "",
          notify_leads: data.notify_leads !== false,
          notify_petitions: data.notify_petitions !== false,
        }));
        setHasMailPassword(!!data.has_password);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm_password) {
      setError("New password and confirm password do not match.");
      return;
    }
    if (form.new_password.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: admin.email,
          current_password: form.current_password,
          new_password: form.new_password,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Failed to change password."); return; }
      setSuccess(true);
      setTimeout(() => { logout(); navigate("/admin/login"); }, 2000);
    } catch {
      setError("Connection error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMailSubmit = async (e) => {
    e.preventDefault();
    setMailError("");
    setMailSuccess("");
    if (mailForm.enabled && !mailForm.smtp_password && !hasMailPassword) {
      setMailError("SMTP password is required when mail sending is enabled.");
      return;
    }
    setMailSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/mail-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...mailForm,
          smtp_port: Number(mailForm.smtp_port),
          smtp_from: mailForm.smtp_from || mailForm.smtp_username,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMailError(data.detail || "Failed to save mail settings.");
        return;
      }
      setHasMailPassword(!!data.has_password);
      setMailForm((current) => ({ ...current, smtp_password: "", smtp_from: data.smtp_from || current.smtp_from }));
      setMailSuccess("Mail settings saved.");
    } catch {
      setMailError("Connection error. Please try again.");
    } finally {
      setMailSubmitting(false);
    }
  };

  const inputCls = "w-full px-3.5 py-2.5 pr-11 rounded-lg border text-sm outline-none transition-colors";
  const inputStyle = { borderColor: "rgba(26,92,55,0.2)", backgroundColor: "#F7F5F0", color: "#1C211F" };
  const labelCls = "block text-xs font-medium mb-1.5 uppercase tracking-wider";

  return (
    <div>
      <div className="mb-8">
        <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>Settings</h3>
        <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>Manage your account settings</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <div className="rounded-2xl border p-6" style={{ backgroundColor: "#fff", borderColor: "rgba(26,92,55,0.1)" }}>
          <div className="flex items-center gap-3 mb-6 pb-5 border-b" style={{ borderColor: "rgba(26,92,55,0.08)" }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
              <Lock size={20} weight="duotone" style={{ color: "#1A5C37" }} />
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: "#1C211F" }}>Change Password</p>
              <p className="text-xs" style={{ color: "#4A5450" }}>You&apos;ll be signed out after changing</p>
            </div>
          </div>

          {success ? (
            <div className="flex flex-col items-center py-6 text-center">
              <CheckCircle size={36} weight="duotone" style={{ color: "#1A5C37" }} className="mb-3" />
              <p className="text-sm font-medium" style={{ color: "#1C211F" }}>Password changed!</p>
              <p className="text-xs mt-1" style={{ color: "#4A5450" }}>Redirecting to login...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Current Password */}
              <div>
                <label className={labelCls} style={{ color: "#4A5450" }}>Current Password</label>
                <div className="relative">
                  <input
                    required type={show.current ? "text" : "password"} placeholder="Current password"
                    value={form.current_password} onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))}
                    className={inputCls} style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                    onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
                  />
                  <button type="button" onClick={() => setShow((s) => ({ ...s, current: !s.current }))} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#7A8A84" }}>
                    {show.current ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className={labelCls} style={{ color: "#4A5450" }}>New Password</label>
                <div className="relative">
                  <input
                    required type={show.new ? "text" : "password"} placeholder="New password"
                    value={form.new_password} onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
                    className={inputCls} style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                    onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
                  />
                  <button type="button" onClick={() => setShow((s) => ({ ...s, new: !s.new }))} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#7A8A84" }}>
                    {show.new ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className={labelCls} style={{ color: "#4A5450" }}>Confirm Password</label>
                <div className="relative">
                  <input
                    required type={show.confirm ? "text" : "password"} placeholder="Confirm new password"
                    value={form.confirm_password} onChange={(e) => setForm((f) => ({ ...f, confirm_password: e.target.value }))}
                    className={inputCls} style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                    onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
                  />
                  <button type="button" onClick={() => setShow((s) => ({ ...s, confirm: !s.confirm }))} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#7A8A84" }}>
                    {show.confirm ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}>{error}</p>
              )}

              <button
                type="submit" disabled={submitting}
                className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors mt-2"
                style={{ backgroundColor: submitting ? "#4a8c62" : "#1A5C37", color: "#F7F5F0", cursor: submitting ? "not-allowed" : "pointer" }}
              >
                {submitting ? "Saving..." : "Change Password"}
              </button>
            </form>
          )}
        </div>

        <div className="rounded-2xl border p-6" style={{ backgroundColor: "#fff", borderColor: "rgba(26,92,55,0.1)" }}>
          <div className="flex items-center gap-3 mb-6 pb-5 border-b" style={{ borderColor: "rgba(26,92,55,0.08)" }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
              <EnvelopeSimple size={20} weight="duotone" style={{ color: "#1A5C37" }} />
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: "#1C211F" }}>Mail Sending</p>
              <p className="text-xs" style={{ color: "#4A5450" }}>GoDaddy webmail SMTP notifications</p>
            </div>
          </div>

          <form onSubmit={handleMailSubmit} className="space-y-4">
            <label className="flex items-center gap-2 text-sm" style={{ color: "#1C211F" }}>
              <input
                type="checkbox"
                checked={mailForm.enabled}
                onChange={(e) => setMailForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
              Enable form notification emails
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} style={{ color: "#4A5450" }}>SMTP Host</label>
                <input
                  required
                  value={mailForm.smtp_host}
                  onChange={(e) => setMailForm((f) => ({ ...f, smtp_host: e.target.value }))}
                  className={inputCls}
                  style={inputStyle}
                  placeholder="smtpout.secureserver.net"
                />
              </div>
              <div>
                <label className={labelCls} style={{ color: "#4A5450" }}>SMTP Port</label>
                <input
                  required
                  type="number"
                  value={mailForm.smtp_port}
                  onChange={(e) => setMailForm((f) => ({ ...f, smtp_port: e.target.value }))}
                  className={inputCls}
                  style={inputStyle}
                  placeholder="465"
                />
              </div>
            </div>

            <div>
              <label className={labelCls} style={{ color: "#4A5450" }}>Security</label>
              <select
                value={mailForm.smtp_security}
                onChange={(e) => setMailForm((f) => ({ ...f, smtp_security: e.target.value }))}
                className={inputCls}
                style={inputStyle}
              >
                <option value="ssl">SSL (GoDaddy usually port 465)</option>
                <option value="tls">TLS / STARTTLS (usually port 587)</option>
                <option value="none">None</option>
              </select>
            </div>

            <div>
              <label className={labelCls} style={{ color: "#4A5450" }}>SMTP Username</label>
              <input
                required
                type="email"
                value={mailForm.smtp_username}
                onChange={(e) => setMailForm((f) => ({ ...f, smtp_username: e.target.value }))}
                className={inputCls}
                style={inputStyle}
                placeholder="info@kybia.org"
              />
            </div>

            <div>
              <label className={labelCls} style={{ color: "#4A5450" }}>SMTP Password</label>
              <input
                type="password"
                value={mailForm.smtp_password}
                onChange={(e) => setMailForm((f) => ({ ...f, smtp_password: e.target.value }))}
                className={inputCls}
                style={inputStyle}
                placeholder={hasMailPassword ? "Saved password kept unless replaced" : "Mailbox password"}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} style={{ color: "#4A5450" }}>From Email</label>
                <input
                  type="email"
                  value={mailForm.smtp_from}
                  onChange={(e) => setMailForm((f) => ({ ...f, smtp_from: e.target.value }))}
                  className={inputCls}
                  style={inputStyle}
                  placeholder="info@kybia.org"
                />
              </div>
              <div>
                <label className={labelCls} style={{ color: "#4A5450" }}>Send Notifications To</label>
                <input
                  required
                  type="email"
                  value={mailForm.mail_to}
                  onChange={(e) => setMailForm((f) => ({ ...f, mail_to: e.target.value }))}
                  className={inputCls}
                  style={inputStyle}
                  placeholder="info@kybia.org"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm" style={{ color: "#4A5450" }}>
                <input
                  type="checkbox"
                  checked={mailForm.notify_leads}
                  onChange={(e) => setMailForm((f) => ({ ...f, notify_leads: e.target.checked }))}
                />
                Notify on leads
              </label>
              <label className="flex items-center gap-2 text-sm" style={{ color: "#4A5450" }}>
                <input
                  type="checkbox"
                  checked={mailForm.notify_petitions}
                  onChange={(e) => setMailForm((f) => ({ ...f, notify_petitions: e.target.checked }))}
                />
                Notify on petitions
              </label>
            </div>

            {mailError && (
              <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}>{mailError}</p>
            )}
            {mailSuccess && (
              <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#1A5C37", backgroundColor: "rgba(26,92,55,0.08)" }}>{mailSuccess}</p>
            )}
            <button
              type="submit"
              disabled={mailSubmitting}
              className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors mt-2 flex items-center justify-center gap-2"
              style={{ backgroundColor: mailSubmitting ? "#4a8c62" : "#1A5C37", color: "#F7F5F0", cursor: mailSubmitting ? "not-allowed" : "pointer" }}
            >
              <FloppyDisk size={16} weight="duotone" />
              {mailSubmitting ? "Saving..." : "Save Mail Settings"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
